
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Lock, UserPlus } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES } from '@/lib/constants';

interface AuthFormProps {
  isSignUp: boolean;
  isLoading: boolean;
  validationError: string;
  formData: {
    email: string;
    password: string;
    username: string;
    role: 'traveler' | 'host';
    nationality?: string;
    agreedToTerms?: boolean;
  };
  onSubmit: (e: React.FormEvent) => void;
  onChange: (field: string, value: string | boolean) => void;
  onToggleMode: () => void;
  onForgotPassword: () => void;
}

export const AuthForm = ({
  isSignUp,
  isLoading,
  validationError,
  formData,
  onSubmit,
  onChange,
  onToggleMode,
  onForgotPassword,
}: AuthFormProps) => {
  const [open, setOpen] = useState(false);

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {validationError && (
        <Alert variant="destructive">
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {isSignUp && (
        <>
          <div className="space-y-2">
            <label htmlFor="username" className="text-sm font-medium">
              Username
            </label>
            <div className="relative">
              <UserPlus className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <Input
                id="username"
                placeholder="Choose a username"
                className="pl-10"
                value={formData.username}
                onChange={(e) => onChange('username', e.target.value)}
                required={isSignUp}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Nationality (optional)</Label>
            <Select
              value={formData.nationality || undefined}
              onValueChange={(value) => onChange('nationality', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select your nationality (optional)" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">I want to</Label>
            <RadioGroup
              value={formData.role}
              onValueChange={(value: 'traveler' | 'host') => onChange('role', value)}
              className="grid grid-cols-2 gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="traveler" id="traveler" />
                <Label htmlFor="traveler">Travel</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="host" id="host" />
                <Label htmlFor="host">Host</Label>
              </div>
            </RadioGroup>
          </div>
        </>
      )}

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium">
          Email
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            className="pl-10"
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="password" className="text-sm font-medium">
          Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <Input
            id="password"
            type="password"
            placeholder={isSignUp ? "Choose a strong password" : "Enter your password"}
            className="pl-10"
            value={formData.password}
            onChange={(e) => onChange('password', e.target.value)}
            required={isSignUp}
            minLength={isSignUp ? 6 : undefined}
          />
          {isSignUp && (
            <p className="text-sm text-gray-500 mt-1">
              Password must be at least 6 characters long, contain one uppercase letter and one number
            </p>
          )}
        </div>
      </div>

      {isSignUp && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="terms" 
              checked={formData.agreedToTerms}
              onCheckedChange={(checked) => onChange('agreedToTerms', !!checked)} 
              required
            />
            <Label htmlFor="terms" className="text-sm">
              I agree to the{" "}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>,{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, and{" "}
              <Link to="/content-policy" className="text-primary hover:underline">User Content Policy</Link>
            </Label>
          </div>
        </div>
      )}

      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading || (isSignUp && !formData.agreedToTerms)}
      >
        {isLoading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
      </Button>

      <div className="text-center space-y-2">
        {!isSignUp && (
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:underline block w-full"
          >
            Forgot your password?
          </button>
        )}
        <button
          type="button"
          onClick={onToggleMode}
          className="text-sm text-primary hover:underline"
        >
          {isSignUp 
            ? 'Already have an account? Sign in' 
            : "Don't have an account? Sign up"}
        </button>
      </div>
    </form>
  );
};
